import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, FileUp, Loader2, MessageSquare, Plus, Save, Trash2 } from "lucide-react";
import type {
  GuideAssetDto,
  GuideDocumentDto,
  SupportDetailFieldDto,
  SupportKbEntryDto,
} from "@mvk/shared";
import { useAdminEvent } from "../hooks/useAdminEvent";
import { supportBotService } from "../../services/supportBotService";
import { formatApiError } from "../../api/client";

const EMPTY_KB = { question: "", answer: "", synonyms: "", block: "Детали выставки" };

const ASSET_TAG_OPTIONS: Array<{ value: GuideAssetDto["tag"]; label: string }> = [
  { value: "parking_map", label: "Схема парковки" },
  { value: "venue_map", label: "План площадки" },
  { value: "entry", label: "Схема входа / КПП" },
  { value: "other", label: "Другое" },
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const b64 = result.includes(",") ? result.split(",")[1]! : result;
      resolve(b64);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}

export function AdminSupportBotEventPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { event, loading: eventLoading } = useAdminEvent(id);

  const [fields, setFields] = useState<SupportDetailFieldDto[]>([]);
  const [details, setDetails] = useState<Record<string, string>>({});
  const [eventKb, setEventKb] = useState<SupportKbEntryDto[]>([]);
  const [documents, setDocuments] = useState<GuideDocumentDto[]>([]);
  const [assets, setAssets] = useState<GuideAssetDto[]>([]);
  const [assetTag, setAssetTag] = useState<GuideAssetDto["tag"]>("parking_map");
  const [previewWelcome, setPreviewWelcome] = useState("");
  const [entriesCount, setEntriesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  /** Details/KB hidden — knowledge comes from documents + neuro; tabs left for rare ops via URL ?advanced=1 */
  const showAdvancedTabs =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("advanced") === "1";
  const [tab, setTab] = useState<"details" | "kb" | "docs" | "preview">(
    showAdvancedTabs ? "details" : "preview",
  );
  const [kbForm, setKbForm] = useState(EMPTY_KB);
  const [previewQ, setPreviewQ] = useState("");
  const [previewA, setPreviewA] = useState("");
  const [previewRole, setPreviewRole] = useState<"visitor" | "exhibitor">("visitor");
  const [previewMeta, setPreviewMeta] = useState<{
    source?: string;
    model?: string | null;
  } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const [data, docsRes, assetsRes] = await Promise.all([
        supportBotService.getEventBot(id),
        supportBotService.listDocuments(id).catch(() => ({ documents: [] as GuideDocumentDto[] })),
        supportBotService.listAssets(id).catch(() => ({ assets: [] as GuideAssetDto[] })),
      ]);
      setFields(data.fields);
      setDetails(data.details);
      setEventKb(data.eventKb);
      setPreviewWelcome(data.resolvedPreview.welcomeMessage);
      setEntriesCount(data.resolvedPreview.entriesCount);
      setDocuments(docsRes.documents);
      setAssets(assetsRes.assets);
    } catch (err) {
      setError(formatApiError(err, "Не удалось загрузить настройки бота"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveDetails = async () => {
    if (!id) return;
    setSaving(true);
    setError("");
    try {
      const res = await supportBotService.saveEventDetails(id, details);
      setDetails(res.details);
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось сохранить детали"));
    } finally {
      setSaving(false);
    }
  };

  const addKb = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await supportBotService.createEventKb(id, kbForm);
      setKbForm(EMPTY_KB);
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось добавить запись"));
    } finally {
      setSaving(false);
    }
  };

  const removeKb = async (entryId: string) => {
    if (!id || !confirm("Удалить запись?")) return;
    await supportBotService.deleteEventKb(id, entryId);
    await load();
  };

  const onUploadDoc = async (file: File | null) => {
    if (!id || !file) return;
    setSaving(true);
    setError("");
    try {
      const contentBase64 = await fileToBase64(file);
      await supportBotService.uploadDocument(id, {
        filename: file.name,
        contentBase64,
      });
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось загрузить документ"));
    } finally {
      setSaving(false);
    }
  };

  const removeDoc = async (docId: string) => {
    if (!id || !confirm("Удалить документ и его чанки?")) return;
    setSaving(true);
    try {
      await supportBotService.deleteDocument(id, docId);
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось удалить документ"));
    } finally {
      setSaving(false);
    }
  };

  const onUploadAsset = async (file: File | null) => {
    if (!id || !file) return;
    setSaving(true);
    setError("");
    try {
      const contentBase64 = await fileToBase64(file);
      await supportBotService.uploadAsset(id, {
        filename: file.name,
        contentBase64,
        tag: assetTag,
      });
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось загрузить схему"));
    } finally {
      setSaving(false);
    }
  };

  const removeAsset = async (assetId: string) => {
    if (!id || !confirm("Удалить схему?")) return;
    setSaving(true);
    try {
      await supportBotService.deleteAsset(id, assetId);
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось удалить схему"));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!id || !previewQ.trim()) return;
    setSaving(true);
    setPreviewA("");
    setPreviewMeta(null);
    setError("");
    try {
      const res = await supportBotService.preview(id, previewQ.trim(), previewRole);
      setPreviewA(res.reply);
      setPreviewMeta({ source: res.source, model: res.model });
    } catch (err) {
      setError(formatApiError(err, "Превью не удалось"));
    } finally {
      setSaving(false);
    }
  };

  if (eventLoading || loading) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Загрузка…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/events/${id}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-[20px] text-gray-900">Чат-бот выставки</h1>
          <p className="text-sm text-gray-500">
            {event?.title} · знания из документов и нейросети
            {documents.length ? ` · документов: ${documents.length}` : ""}
            {assets.length ? ` · схем: ${assets.length}` : ""}
            {showAdvancedTabs ? ` · KB: ${entriesCount}` : ""}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          showAdvancedTabs
            ? ([
                ["details", "Детали"],
                ["kb", "Доп. знания"],
                ["docs", "Документы"],
                ["preview", "Превью"],
              ] as const)
            : ([
                ["docs", "Документы"],
                ["preview", "Превью"],
              ] as const)
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-lg text-sm ${tab === k ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700"}`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => navigate(`/events/${id}/support`)}
          className="px-3 py-1.5 rounded-lg text-sm bg-gray-50 text-gray-600 border border-gray-200"
        >
          Ссылки поддержки
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {showAdvancedTabs && tab === "details" && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-gray-500">
            Эти значения подставляются в глобальный пресет вместо{" "}
            <code className="text-xs bg-gray-100 px-1 rounded">{"{{field}}"}</code>.
          </p>
          <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-2">
            Пример приветствия: {previewWelcome || "—"}
          </div>
          {fields.map((f) => (
            <label key={f.id} className="block">
              <span className="text-xs font-medium text-gray-700">
                {f.label}{" "}
                <span className="text-gray-400 font-normal">{"{{" + f.id + "}}"}</span>
              </span>
              {f.description && (
                <span className="block text-[11px] text-gray-400">{f.description}</span>
              )}
              <textarea
                rows={f.id === "extra_notes" || f.id === "parking" || f.id === "contacts_url" ? 3 : 2}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder={f.exampleValue || undefined}
                value={details[f.id] ?? ""}
                onChange={(e) => setDetails({ ...details, [f.id]: e.target.value })}
              />
            </label>
          ))}
          <button
            type="button"
            disabled={saving}
            onClick={() => void saveDetails()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Сохранить детали
          </button>
        </div>
      )}

      {showAdvancedTabs && tab === "kb" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Дополнительные Q&A только для этой выставки (поверх глобальной базы).
          </p>
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <input
              placeholder="Вопрос"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={kbForm.question}
              onChange={(e) => setKbForm({ ...kbForm, question: e.target.value })}
            />
            <textarea
              placeholder="Ответ"
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={kbForm.answer}
              onChange={(e) => setKbForm({ ...kbForm, answer: e.target.value })}
            />
            <button
              type="button"
              disabled={saving || !kbForm.question || !kbForm.answer}
              onClick={() => void addKb()}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
            >
              <Plus size={14} /> Добавить
            </button>
          </div>
          {eventKb.map((e) => (
            <div key={e.id} className="bg-white border border-gray-200 rounded-xl p-3 text-sm">
              <div className="font-medium">{e.question}</div>
              <div className="text-gray-600 mt-1 whitespace-pre-wrap">{e.answer}</div>
              <button
                type="button"
                className="text-red-600 text-xs mt-2 inline-flex items-center gap-1"
                onClick={() => void removeKb(e.id)}
              >
                <Trash2 size={12} /> Удалить
              </button>
            </div>
          ))}
          {!eventKb.length && (
            <div className="text-sm text-gray-400">Пока нет дополнительных записей.</div>
          )}
        </div>
      )}

      {tab === "docs" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Manual / infoletter / forms привязываются к выставке. Словарь и FAQ — к группе (Synergy), если
            выставка в группе. Текст режется на чанки и подмешивается в нейрогид (не требует формата
            «Вопрос/Ответ»).
          </p>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm cursor-pointer">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
            Загрузить PDF / DOCX / PPTX
            <input
              type="file"
              accept=".pdf,.docx,.pptx,.txt,.md,application/pdf"
              className="hidden"
              disabled={saving}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = "";
                void onUploadDoc(f);
              }}
            />
          </label>
          <div className="space-y-2">
            {documents.map((d) => (
              <div
                key={d.id}
                className="bg-white border border-gray-200 rounded-xl p-3 text-sm flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.title || d.sourceFilename}</div>
                  <div className="text-[12px] text-gray-500 mt-0.5">
                    {d.docType} · {d.lang} · {d.chunkCount} чанков ·{" "}
                    {d.groupId ? "группа" : "выставка"} · {Math.round(d.bytes / 1024)} KB
                  </div>
                  {d.status === "error" && d.errorMessage ? (
                    <div className="text-[12px] text-red-600 mt-1">{d.errorMessage}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="text-red-600 text-xs inline-flex items-center gap-1 flex-shrink-0"
                  onClick={() => void removeDoc(d.id)}
                >
                  <Trash2 size={12} /> Удалить
                </button>
              </div>
            ))}
            {!documents.length && (
              <div className="text-sm text-gray-400">
                Документов пока нет — загрузите руководство или инфописьмо.
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-sm text-gray-500">
              Схемы для чата (парковка, план площадки, вход). При вопросе с нужным смыслом гид
              покажет картинку вместе с текстовым ответом.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={assetTag}
                onChange={(e) => setAssetTag(e.target.value as GuideAssetDto["tag"])}
              >
                {ASSET_TAG_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm cursor-pointer">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
                Загрузить PNG / JPG
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                  className="hidden"
                  disabled={saving}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    e.target.value = "";
                    void onUploadAsset(f);
                  }}
                />
              </label>
            </div>
            <div className="space-y-2">
              {assets.map((a) => (
                <div
                  key={a.id}
                  className="bg-white border border-gray-200 rounded-xl p-3 text-sm flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.title || a.tag}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">
                      {ASSET_TAG_OPTIONS.find((o) => o.value === a.tag)?.label ?? a.tag} ·{" "}
                      {a.groupId ? "группа" : "выставка"} · {Math.round(a.bytes / 1024)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-red-600 text-xs inline-flex items-center gap-1 flex-shrink-0"
                    onClick={() => void removeAsset(a.id)}
                  >
                    <Trash2 size={12} /> Удалить
                  </button>
                </div>
              ))}
              {!assets.length && (
                <div className="text-sm text-gray-400">Схем пока нет — загрузите картинку с тегом.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "preview" && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <MessageSquare size={16} /> Живой ответ нейросети (как в приложении)
          </div>
          <p className="text-[12px] text-gray-500">
            Переключайте роль и смотрите, что увидит посетитель или участник. Источник ответа
            помечается: нейросеть / база знаний / запасной.
          </p>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["visitor", "Посетитель"],
                ["exhibitor", "Участник"],
              ] as const
            ).map(([role, label]) => (
              <button
                key={role}
                type="button"
                onClick={() => {
                  setPreviewRole(role);
                  setPreviewA("");
                  setPreviewMeta(null);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  previewRole === role
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            placeholder={
              previewRole === "exhibitor"
                ? "Вопрос участника (монтаж, пропуска, парковка…)"
                : "Вопрос посетителя (компании, программа, карта…)"
            }
            value={previewQ}
            onChange={(e) => setPreviewQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runPreview();
            }}
          />
          <button
            type="button"
            disabled={saving || !previewQ.trim()}
            onClick={() => void runPreview()}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Спросить нейросеть
          </button>
          {previewA && (
            <div className="space-y-2">
              {previewMeta ? (
                <div className="text-[11px] text-gray-500 flex flex-wrap gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-gray-100">
                    {previewMeta.source === "llm"
                      ? "нейросеть"
                      : previewMeta.source === "kb"
                        ? "база знаний / документы"
                        : previewMeta.source === "fallback"
                          ? "запасной ответ"
                          : previewMeta.source || "ответ"}
                  </span>
                  {previewMeta.model ? (
                    <span className="px-1.5 py-0.5 rounded bg-gray-100">{previewMeta.model}</span>
                  ) : null}
                  <span className="px-1.5 py-0.5 rounded bg-gray-100">
                    {previewRole === "exhibitor" ? "участник" : "посетитель"}
                  </span>
                </div>
              ) : null}
              <div className="text-sm whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-gray-800">
                {previewA}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
